/**
 * react-hook-form resolver for the wizard. The schema depends on the current
 * environment and customer number (VM-name rules) and deployment model (shared
 * or dedicated DB/gateway variant), so it is rebuilt for every validation pass
 * from the values being validated.
 */
import { zodResolver } from '@hookform/resolvers/zod'
import type { Resolver } from 'react-hook-form'
import type { WorkwiseFormValues } from '../../types/workwise'
import { buildFormSchema } from './schema'

function schemaFor(values: WorkwiseFormValues) {
  return buildFormSchema({
    env: values.environment,
    customerNumber: values.customer?.number ?? '',
    model: values.deploymentModel === 'dedicated' ? 'dedicated' : 'saas-hybrid',
    skipApplication: values.skipApplication === true,
  })
}

export const workwiseResolver: Resolver<WorkwiseFormValues> = (values, context, options) =>
  zodResolver(schemaFor(values))(values, context, options)

/**
 * Validates the whole form outside react-hook-form and returns the flat
 * { "dotted.path": ["message"] } problems, so the wizard can jump to the first
 * step that has any.
 */
export function validateForm(values: WorkwiseFormValues): Record<string, string[]> {
  const result = schemaFor(values).safeParse(values)
  const problems: Record<string, string[]> = {}
  if (!result.success) {
    for (const issue of result.error.issues) {
      ;(problems[issue.path.join('.') || 'form'] ??= []).push(issue.message)
    }
  }
  return problems
}
